import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import RefreshIcon from "@mui/icons-material/Refresh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockResetIcon from "@mui/icons-material/LockReset";
import PasswordIcon from "@mui/icons-material/Password";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TimelineIcon from "@mui/icons-material/Timeline";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  getUsersByTenant,
  setUserBlocked,
  resendVerificationEmail,
  deleteAuth0User,
  updateAuth0User,
  getRolesByTenantId,
} from "./api/tenantApi";
import { getRoleRecordId } from "./utils/rolePermissions";
import { isPlatformAdmin } from "./utils/isPlatformAdmin";
import JourneyDrawer from "./analytics/JourneyDrawer";
import AddUserModal from "./AddUserModal";
import InviteUsersModal from "./InviteUsersModal";
import TemporaryPasswordDialog from "./TemporaryPasswordDialog";
import { sendTenantPasswordReset } from "./utils/inviteHelper";

const BLUE = "#1976d2";

const initials = (name, email) => {
  const s = (name || email || "?").trim();
  const parts = s.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
};

const fmtDate = (v) => {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

// ---- Edit dialog: name, role, admin ---------------------------------------
function EditUserDialog({ open, user, roles, tenantId, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [roleId, setRoleId] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setRoleId(user.roleId || "");
      setIsAdmin(!!user.isAdmin);
    }
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const payload = {
        name: name || undefined,
        user_metadata: {
          tenantId,
          roleId: roleId || null,
          isAdmin,
        },
      };
      await updateAuth0User(user.id, payload);
      toast.success("User updated");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.details || e?.message || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Edit user</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: "#64748b", mb: 2 }}>{user?.email}</Typography>
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth margin="dense" />
        <FormControl fullWidth margin="dense">
          <InputLabel id="edit-role-label">Role</InputLabel>
          <Select
            labelId="edit-role-label"
            label="Role"
            value={roleId || ""}
            onChange={(e) => setRoleId(e.target.value)}
          >
            <MenuItem value="">
              <em>Default user</em>
            </MenuItem>
            {roles.map((r) => {
              const id = getRoleRecordId(r);
              return (
                <MenuItem key={id} value={id}>
                  {r.name}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <FormControlLabel
          sx={{ mt: 1 }}
          control={<Switch checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />}
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <ShieldOutlinedIcon sx={{ fontSize: 18, color: isAdmin ? BLUE : "#94a3b8" }} />
              <span>Platform admin</span>
            </Box>
          }
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving} sx={{ textTransform: "none" }}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---- Confirm dialog --------------------------------------------------------
function ConfirmDialog({ open, title, body, confirmLabel, danger, busy, onCancel, onConfirm }) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, color: "#334155" }}>{body}</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={danger ? "error" : "primary"}
          disabled={busy}
          sx={{ textTransform: "none" }}
        >
          {busy ? "Working…" : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function TenantUsersModal({ open, onClose, tenant }) {
  const tenantId = tenant?.id;
  const tenantName = tenant?.name || tenant?.companyName || "Tenant";

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuUser, setMenuUser] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState(null); // { type, user }
  const [journeyUser, setJourneyUser] = useState(null);
  const [passwordUser, setPasswordUser] = useState(null);

  const viewerIsAdmin = useSelector((s) => s.userInfo.isAdmin);
  const viewerEmail = useSelector((s) => s.userInfo.email);
  const canSeeJourney = isPlatformAdmin(viewerEmail, viewerIsAdmin);

  const roleName = useCallback(
    (u) => {
      // Tenant admins outrank any assigned role — surface that first.
      if (u?.isTenantAdmin) return "Tenant Admin";
      const id = u?.roleId;
      if (!id) return "Default user";
      const r = roles.find((x) => getRoleRecordId(x) === id);
      return r?.name || "Custom role";
    },
    [roles]
  );

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [uRes, rRes] = await Promise.all([
        getUsersByTenant(tenantId),
        getRolesByTenantId(tenantId).catch(() => ({ data: [] })),
      ]);
      setUsers(uRes?.data || []);
      setRoles(rRes?.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.details || e?.message || "Failed to load users");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (open && tenantId) load();
    if (!open) {
      setUsers([]);
      setQuery("");
    }
  }, [open, tenantId, load]);

  const filtered = useMemo(() => {
    const t = query.trim().toLowerCase();
    if (!t) return users;
    return users.filter((u) => `${u.name} ${u.email} ${roleName(u)}`.toLowerCase().includes(t));
  }, [users, query, roleName]);

  const openMenu = (e, user) => {
    setMenuAnchor(e.currentTarget);
    setMenuUser(user);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuUser(null);
  };

  const doBlockToggle = async (user) => {
    closeMenu();
    setBusyId(user.id);
    try {
      await setUserBlocked(user.id, !user.blocked);
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, blocked: !user.blocked } : u)));
      toast.success(user.blocked ? "User unblocked" : "User blocked");
    } catch (e) {
      toast.error(e?.response?.data?.details || e?.message || "Failed to update user");
    } finally {
      setBusyId(null);
    }
  };

  const doResetPassword = async (user) => {
    closeMenu();
    setBusyId(user.id);
    try {
      const result = await sendTenantPasswordReset(tenant, user.email);
      toast.success(`${result.platform === "carrier" ? "Carrier platform" : "Risk platform"} password reset sent to ${user.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.details || e?.message || "Failed to send reset email");
    } finally {
      setBusyId(null);
    }
  };

  const doResendVerification = async (user) => {
    closeMenu();
    setBusyId(user.id);
    try {
      await resendVerificationEmail(user.id);
      toast.success(`Verification email sent to ${user.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.details || e?.message || "Failed to resend verification");
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async (user) => {
    setBusyId(user.id);
    try {
      await deleteAuth0User(user.id, { tenantId, email: user.email });
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      toast.success("User deleted");
      setConfirm(null);
    } catch (e) {
      toast.error(e?.response?.data?.details || e?.message || "Failed to delete user");
    } finally {
      setBusyId(null);
    }
  };

  const GRID = "2.4fr 1.3fr 1fr 1.1fr 0.9fr 44px";

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: "14px", maxHeight: "88vh" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, pb: 1 }}>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>Manage users</Typography>
            <Typography sx={{ fontSize: 13, color: "#64748b" }}>
              {tenantName} · {users.length} user{users.length === 1 ? "" : "s"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={load} disabled={loading} size="small">
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              onClick={() => setInviteOpen(true)}
              variant="contained"
              startIcon={<MarkEmailReadIcon />}
              sx={{ textTransform: "none", borderRadius: "8px", background: BLUE }}
            >
              Invite
            </Button>
            <Button
              onClick={() => setAddOpen(true)}
              variant="outlined"
              startIcon={<PersonAddIcon />}
              sx={{ textTransform: "none", borderRadius: "8px", borderColor: "#cbd5e1", color: BLUE }}
            >
              Add user
            </Button>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email or role…"
            size="small"
            fullWidth
            sx={{ mb: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ border: "1px solid #e8edf5", borderRadius: "10px", overflow: "hidden" }}>
            {/* header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: GRID,
                gap: 1,
                background: "#f8fafc",
                px: 1.5,
                py: 1,
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              {["User", "Role", "Status", "Last login", "Verified", ""].map((h, i) => (
                <Typography
                  key={i}
                  sx={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color: "#94a3b8" }}
                >
                  {h}
                </Typography>
              ))}
            </Box>

            <Box sx={{ maxHeight: "56vh", overflowY: "auto" }}>
              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6, gap: 1.5 }}>
                  <CircularProgress size={22} />
                  <Typography sx={{ fontSize: 13, color: "#64748b" }}>Loading users…</Typography>
                </Box>
              )}

              {!loading && filtered.length === 0 && (
                <Typography sx={{ fontSize: 13, color: "#94a3b8", textAlign: "center", py: 5 }}>
                  {users.length === 0 ? "No users in this tenant yet." : "No users match your search."}
                </Typography>
              )}

              {!loading &&
                filtered.map((u) => (
                  <Box
                    key={u.id}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: GRID,
                      gap: 1,
                      alignItems: "center",
                      px: 1.5,
                      py: 1.1,
                      borderTop: "1px solid #f1f5f9",
                      opacity: busyId === u.id ? 0.5 : 1,
                      "&:hover": { background: "#f9fbff" },
                    }}
                  >
                    {/* user */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                      <Avatar sx={{ width: 34, height: 34, fontSize: 13, fontWeight: 700, bgcolor: u.blocked ? "#94a3b8" : BLUE }}>
                        {initials(u.name, u.email)}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Typography
                            sx={{ fontSize: 13.5, fontWeight: 600, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          >
                            {u.name || "—"}
                          </Typography>
                          {u.isAdmin && (
                            <Tooltip title="Platform admin">
                              <ShieldOutlinedIcon sx={{ fontSize: 15, color: BLUE }} />
                            </Tooltip>
                          )}
                        </Box>
                        <Typography sx={{ fontSize: 12, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {u.email}
                        </Typography>
                      </Box>
                    </Box>

                    {/* role */}
                    <Typography sx={{ fontSize: 12.5, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {roleName(u)}
                    </Typography>

                    {/* status */}
                    <Box>
                      <Chip
                        size="small"
                        label={u.blocked ? "Blocked" : "Active"}
                        sx={{
                          height: 20,
                          fontSize: 11,
                          fontWeight: 700,
                          color: u.blocked ? "#b91c1c" : "#15803d",
                          background: u.blocked ? "#fee2e2" : "#dcfce7",
                        }}
                      />
                    </Box>

                    {/* last login */}
                    <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>{fmtDate(u.last_login)}</Typography>

                    {/* verified */}
                    <Box>
                      {u.email_verified === null ? (
                        <Typography sx={{ fontSize: 12.5, color: "#cbd5e1" }}>—</Typography>
                      ) : u.email_verified ? (
                        <Tooltip title="Email verified">
                          <CheckCircleIcon sx={{ fontSize: 18, color: "#22c55e" }} />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Email not verified">
                          <MailOutlineIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
                        </Tooltip>
                      )}
                    </Box>

                    {/* actions */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      {busyId === u.id ? (
                        <CircularProgress size={18} />
                      ) : (
                        <IconButton size="small" onClick={(e) => openMenu(e, u)}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ))}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Row action menu */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        {canSeeJourney && (
          <MenuItem
            onClick={() => {
              const u = menuUser;
              closeMenu();
              setJourneyUser({ userId: u.id, email: u.email, name: u.name, tenantId });
            }}
          >
            <ListItemIcon><TimelineIcon fontSize="small" /></ListItemIcon>
            <ListItemText>View journey</ListItemText>
          </MenuItem>
        )}
        {canSeeJourney && <Divider />}
        <MenuItem
          onClick={() => {
            const u = menuUser;
            closeMenu();
            setEditUser(u);
          }}
        >
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit name / role / admin</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => doResetPassword(menuUser)}>
          <ListItemIcon><LockResetIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Send password reset</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { const u = menuUser; closeMenu(); setPasswordUser(u); }}>
          <ListItemIcon><PasswordIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Set temporary password</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => doResendVerification(menuUser)}>
          <ListItemIcon><MailOutlineIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Resend verification email</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => doBlockToggle(menuUser)}>
          <ListItemIcon>
            {menuUser?.blocked ? <CheckCircleIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{menuUser?.blocked ? "Unblock user" : "Block user"}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            const u = menuUser;
            closeMenu();
            setConfirm({ type: "delete", user: u });
          }}
          sx={{ color: "#dc2626" }}
        >
          <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: "#dc2626" }} /></ListItemIcon>
          <ListItemText>Delete user</ListItemText>
        </MenuItem>
      </Menu>

      <JourneyDrawer open={!!journeyUser} user={journeyUser} onClose={() => setJourneyUser(null)} />
      <TemporaryPasswordDialog open={!!passwordUser} user={passwordUser} tenantId={tenantId} tenantName={tenantName} onClose={() => setPasswordUser(null)} />

      <InviteUsersModal
        open={inviteOpen}
        onClose={() => { setInviteOpen(false); load(); }}
        tenant={tenant}
      />

      {/* Add user — reuse existing modal, refresh on close */}
      <AddUserModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
          load();
        }}
        tenantInfo={tenant}
      />

      {/* Edit */}
      <EditUserDialog
        open={!!editUser}
        user={editUser}
        roles={roles}
        tenantId={tenantId}
        onClose={() => setEditUser(null)}
        onSaved={load}
      />

      {/* Delete confirm */}
      <ConfirmDialog
        open={confirm?.type === "delete"}
        title="Delete user"
        body={`Permanently delete ${confirm?.user?.name || confirm?.user?.email}? This removes their login and access to ${tenantName}. This cannot be undone.`}
        confirmLabel="Delete user"
        danger
        busy={busyId === confirm?.user?.id}
        onCancel={() => setConfirm(null)}
        onConfirm={() => doDelete(confirm.user)}
      />
    </>
  );
}
